'use client';

import React, { useMemo, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import { useEstimator } from '../../context/EstimatorContext';
import { CostCategorySummaryGridData } from '../../utils/costCategorySummaryGrid';
import { costCategoryCalculator } from '../../utils/costCategoryCalculator';
import useFetchRowPricing from '../../hooks/useFetchRowPricing';
import useFetchPipePricing from '../../hooks/useFetchPipePricing';
import useFetchCoatingPricing from '../../hooks/useFetchCoatingPricing';
import useFetchComponentPricing from '../../hooks/useFetchComponentPricing';
import useFetchMiscPricing from '../../hooks/useFetchMiscPricing';
import useFetchLineFillPricing from '../../hooks/useFetchLineFillPricing';
import useFetchConstructionPricing from '../../hooks/useFetchConstructionPricing';
import { calculateClassLengths } from '../../utils/hiddenPipeCostCalculator';

const CostCategorySummaryGrid = () => {
  const { formData, setFormData } = useEstimator();
  const { rowPricing } = useFetchRowPricing();
  const { pipePricing } = useFetchPipePricing();
  const { coatingPricing } = useFetchCoatingPricing();
  const { componentPricing } = useFetchComponentPricing();
  const { miscPricing } = useFetchMiscPricing();
  const { lineFillPricing } = useFetchLineFillPricing();
  const { constructionPricing } = useFetchConstructionPricing();

  const [gridData, setGridData] = useState([]);

  const costCategoryPricesKey = useMemo(() => {
    return formData?.costCategory
      ?.slice(14, 21) // inclusive of index 20
      .map((row) => row?.price ?? '')
      .join('|');
  }, [formData?.costCategory]);

  useEffect(() => {
    const updatedRows = CostCategorySummaryGridData.map((row) => {
      const calculator = costCategoryCalculator[row.no];
      if (calculator) {
        const calculated = calculator(
          row,
          formData,
          rowPricing,
          componentPricing,
          miscPricing,
          lineFillPricing,
          constructionPricing
        );
        return { ...row, ...calculated };
      }
      return row;
    });

    // Section 1: Sum for rows 1–13
    let totalContingencyCost = 0;
    let totalBaseCost = 0;
    let totalTotalPrice = 0;

    for (let i = 0; i < updatedRows.length; i++) {
      const row = updatedRows[i];
      if (row.no >= 1 && row.no <= 13) {
        totalContingencyCost += isNaN(parseFloat(row.contingencyCost)) ? 0 : parseFloat(row.contingencyCost);
        totalBaseCost += isNaN(parseFloat(row.baseCost)) ? 0 : parseFloat(row.baseCost);
        totalTotalPrice += isNaN(parseFloat(row.totalPrice)) ? 0 : parseFloat(row.totalPrice);
      }
    }

    // Update row 14 (summary row for 1–13)
    const summaryIndex = updatedRows.findIndex((row) => row.no === 14);
    if (summaryIndex !== -1) {
      updatedRows[summaryIndex] = {
        ...updatedRows[summaryIndex],
        contingencyCost: totalContingencyCost,
        baseCost: totalBaseCost,
        totalPrice: totalTotalPrice,
        isSummary: true,
      };
    }

    // Section 2: Sum for rows 15–28
    let section2Contingency = 0;
    let section2Base = 0;
    let section2Total = 0;

    for (let i = 0; i < updatedRows.length; i++) {
      const row = updatedRows[i];
      if (row.no >= 15 && row.no <= 28) {
        section2Contingency += isNaN(parseFloat(row.contingencyCost)) ? 0 : parseFloat(row.contingencyCost);
        section2Base += isNaN(parseFloat(row.baseCost)) ? 0 : parseFloat(row.baseCost);
        section2Total += isNaN(parseFloat(row.totalPrice)) ? 0 : parseFloat(row.totalPrice);
      }
    }

    // Example: log or store it in row 29 if that row exists
    const summary2Index = updatedRows.findIndex((row) => row.no === 29);
    if (summary2Index !== -1) {
      updatedRows[summary2Index] = {
        ...updatedRows[summary2Index],
        contingencyCost: section2Contingency,
        baseCost: section2Base,
        totalPrice: section2Total,
        isSummary: true,
      };
    }

    const pipeODQuantity = formData.quantities?.[0]?.quantity;
    const pipeLengthByClass = calculateClassLengths(formData.pipeSpecs, pipeODQuantity, pipePricing);

    setGridData(updatedRows);
    setFormData({
      ...formData,
      costCategory: updatedRows,
      hiddenPipeCostCalc: pipeLengthByClass,
    });
  }, [
    formData.quantities,
    formData.costAssumptions,
    formData.averageROWCostPerFoot,
    formData.pipeSpecs,
    costCategoryPricesKey,
    pipePricing,
    coatingPricing,
    rowPricing,
    componentPricing,
    miscPricing,
    lineFillPricing,
    constructionPricing,
  ]);

  const columnDefs = useMemo(
    () => [
      { headerName: 'Account', field: 'account', flex: 1 },
      { headerName: 'Category', field: 'category', flex: 1 },
      { headerName: 'Description', field: 'description', flex: 1 },
      {
        headerName: 'PM Override',
        field: 'pmOverride',
        editable: true,
        flex: 1,
        cellStyle: { backgroundColor: '#f0f0f0', color: '#000' },
        valueFormatter: ({ value }) =>
          value !== undefined && value !== ''
            ? `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
            : '',
      },
      {
        headerName: 'Price',
        field: 'price',
        flex: 1,
        valueFormatter: ({ value }) =>
          value !== undefined && value !== ''
            ? `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
            : '',
      },
      {
        headerName: '% Contingency',
        field: 'contingencyPercent',
        flex: 1,
        valueFormatter: ({ value }) => (value !== undefined && value !== '' ? `${parseFloat(value)}%` : ''),
        editable: false,
      },
      {
        headerName: '% Cont Override',
        field: 'contOverridePercent',
        editable: true,
        flex: 1,
        cellStyle: { backgroundColor: '#f0f0f0', color: '#000' },
      },
      {
        headerName: 'Contingency Cost',
        field: 'contingencyCost',
        flex: 1,
        valueFormatter: ({ value }) =>
          value !== undefined && value !== ''
            ? `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
            : '',
      },
      {
        headerName: 'Tax Rate',
        field: 'taxRate',
        flex: 1,
        valueFormatter: ({ value }) => (value !== undefined && value !== '' ? `${parseFloat(value)}%` : ''),
      },
      {
        headerName: 'Tax Cost',
        field: 'taxCost',
        flex: 1,
        valueFormatter: ({ value }) =>
          value !== undefined && value !== ''
            ? `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
            : '',
      },
      {
        headerName: 'Base Cost',
        field: 'baseCost',
        flex: 1,
        valueFormatter: ({ value }) =>
          value !== undefined && value !== ''
            ? `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
            : '',
      },
      {
        headerName: 'Total Price',
        field: 'totalPrice',
        flex: 1,
        valueFormatter: ({ value }) =>
          value !== undefined && value !== ''
            ? `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
            : '',
      },
    ],
    []
  );

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header with Title and Menu */}
      <Typography
        variant="h6"
        sx={{
          backgroundColor: '#3A5B8A',
          color: 'white',
          padding: '8px 16px',
          borderRadius: 1,
        }}
      >
        Cost Category Summary
      </Typography>

      {/* Main Cost Category Grid */}
      <Box
        className="ag-theme-alpine"
        sx={{
          width: '100%',
          overflowX: 'auto',
          borderRadius: 1,
          boxShadow: 1,
        }}
      >
        <AgGridReact
          rowData={gridData}
          columnDefs={columnDefs}
          defaultColDef={{
            resizable: true,
            editable: false,
            cellStyle: {
              textAlign: 'left',
              fontSize: '12px',
              padding: '2px',
              borderRight: '0.1px solid #ccc',
            },
          }}
          domLayout="autoHeight"
          suppressMovableColumns
          getRowStyle={(params) => {
            if (params.data?.isSummary) {
              return {
                backgroundColor: '#e3f2fd',
                fontWeight: 'bold',
                fontSize: '13px',
                color: '#1e88e5',
                borderTop: '2px solid #90caf9',
                borderBottom: '2px solid #90caf9',
              };
            }
            return {
              fontSize: '12px',
              lineHeight: '1.2',
              padding: '2px 4px',
            };
          }}
          onCellValueChanged={(event) => {
            const updated = [...gridData];
            const rowIndex = event.rowIndex;
            const field = event.colDef.field;
            const newValue = event.newValue;

            updated[rowIndex] = {
              ...updated[rowIndex],
              [field]: newValue,
            };

            const updatedFormData = {
              ...formData,
              costCategory: updated,
            };

            const calculator = costCategoryCalculator[updated[rowIndex].no];
            if (calculator) {
              const recalculated = calculator(
                updated[rowIndex],
                updatedFormData,
                rowPricing,
                componentPricing,
                miscPricing,
                lineFillPricing,
                constructionPricing
              );
              updated[rowIndex] = { ...updated[rowIndex], ...recalculated };
            }

            setGridData(updated);
            setFormData(updatedFormData);
          }}
        />
      </Box>
    </Box>
  );
};

export default CostCategorySummaryGrid;
